import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDataEntryContext } from "../context/reducer.provider";
import { useTimerContext } from "../context/timer.provider";
import {
  Addition,
  Heating,
  Probe,
  Process,
  ProcessResponse,
} from "../models/lmf.models";
import {
  UpsertAddition,
  UpsertHeating,
  changeLMFHeatStatus,
  deleteAdditionById,
  deleteProbeById,
  getLMFProcess,
  upsertHeat,
  upsertPlugMetrics,
  upsertProbe,
  upsertProcess,
  upsertProcessData,
  UpsertAdditionParams,
  UpsertPlugMetricsParams,
  readPlugFieldFromPLC,
} from "../repositories/lmf.repositories";

export const useLMFDataEntryActions = () => {
  const navigate = useNavigate();

  const {
    state,
    actions: {
      setDEProcess,
      setErrorMessage,
      setIsDataLoading,
      setIsSubmitLoading,
      setNotificationMessage,
      setLastUpdate,
    },
  } = useDataEntryContext();

  const { restart } = useTimerContext();

  const loadData = async (processId: number | null) => {
    setIsDataLoading(true);
    const resp = await getLMFProcess({ ProcessId: processId });
    if (resp.ok) {
      const data = resp.data as ProcessResponse;
      const process = data.tables["0"].rows[0] as Process;
      setDEProcess({
        process,
        probes: data.tables["1"].rows as Probe[],
        heatings: data.tables["2"].rows,
        additions: data.tables["3"].rows,
        slags: data.tables["4"].rows,
        missingFields: data.tables["5"]?.rows ?? [],
      });
      setLastUpdate();
      navigate(`/lmf-data-entry/${process.ProcessId}`);
    } else {
      setErrorMessage(resp.message!);
    }
    setIsDataLoading(false);
  };

  const refreshData = useCallback(async () => {
    if (state.deProcess?.process?.ProcessId) {
      loadData(state.deProcess.process.ProcessId).then(() => {
        restart();
      });
    } else {
      loadData(null).then(() => {
        restart();
      });
    }
  }, [state.deProcess?.process?.ProcessId]);

  const saveProcess = async (
    process: Process,
    opts?: { isShipManual?: boolean },
  ) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProcess({
      ProcessId: process.ProcessId!,
      Start: process.Start!.toISOString(),
      End: process.End?.toISOString() || null,
      IsShipManual: opts?.isShipManual,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
    return resp.ok;
  };

  const saveProcessData = async ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");

    const processedValue = value instanceof Date ? value.toISOString() : value;

    const resp = await upsertProcessData({
      processId,
      field,
      value: processedValue,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const changeHeatStatus = async (
    heatId: number,
    status: string,
    ladleCar?: number,
  ) => {
    setIsSubmitLoading(true);
    const resp = await changeLMFHeatStatus({
      heatId,
      status,
      ladleCar,
    });
    if (resp.ok) {
      const data = resp.data;
      await loadData(data[0].ProcessId);
      return data;
    } else {
      setErrorMessage(resp.message!);
    }
    setIsSubmitLoading(false);
  };

  const saveProbe = async (probe: Probe) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProbe({
      ProcessId: state.deProcess?.process!.ProcessId!,
      ProbeId: probe.ProbeId!,
      IsProbeManual: true,
      Timestamp: probe.Timestamp!.toISOString(),
      Temperature: probe.Temperature,
      Carbon: probe.Carbon,
      Oxygen: probe.Oxygen,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeat = async ({
    heatId,
    field,
    value,
  }: {
    heatId: number;
    field: string;
    value: any;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertHeat({
      HeatId: heatId,
      field: field,
      value: value,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeating = async (heating: Heating) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await UpsertHeating({
      ProcessId: state.deProcess?.process!.ProcessId!,
      HeatingId: heating.HeatingId,
      StartTime: heating.StartTime!.toISOString(),
      EndTime: heating.EndTime!.toISOString(),
      Energy: heating.Energy,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Heating saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveAddition = async (addition: Addition) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await UpsertAddition({
      ProcessId: state.deProcess?.process!.ProcessId!,
      AdditionId: addition?.AdditionId,
      Timestamp: addition?.Timestamp,
      Weight: addition?.Weight!,
      MaterialId: addition?.MaterialId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Addition saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteProbe = async (probeId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteProbeById({
      ProbeId: probeId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Probe deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteAddition = async (additionId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteAdditionById({
      AdditionId: additionId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Addition deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const savePlugMetrics = async (params: UpsertPlugMetricsParams) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertPlugMetrics(params);
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Plug metrics saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const revertPlugFieldFromPLC = async (field: string) => {
    if (!state.deProcess?.process?.ProcessId) return;
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await readPlugFieldFromPLC({
      ProcessId: state.deProcess.process.ProcessId,
      Field: field,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Field reverted from PLC successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  return {
    saveProcess,
    loadData,
    refreshData,
    saveProcessData,
    saveProbe,
    saveHeat,
    saveHeating,
    saveAddition,
    deleteProbe,
    deleteAddition,
    changeHeatStatus,
    savePlugMetrics,
    revertPlugFieldFromPLC,
  };
};
